import React from 'react';
import { Handle, Position } from '@xyflow/react';
import IVRNodeLabel from './NodeLabels/IVRNodeLabel';
import QueueNodeLabel from './NodeLabels/QueueNodeLabel';
import PromptNodeLabel from './NodeLabels/PromptNodeLabel';
import TransferNodeLabel from './NodeLabels/TransferNodeLabel';
import DisconnectNodeLabel from './NodeLabels/DisconnectNodeLabel';
import RoutingNodeLabel from './NodeLabels/RoutingNodeLabel';
import {
  buildRoutingGraph,
  calculateLevels,
  organizeByLevels,
  calculateHierarchicalPositions
} from './HierarchicalLayout';

/**
 * Wrapper pour ajouter des handles multiples aux nœuds
 */
const NodeWithHandles = ({ children, childCount = 0, isPrompt = false, isQueue = false }) => {
  // Pour Prompt, toujours afficher au moins 1 handle de sortie (pour "Next")
  // Pour Queue, ne pas afficher de handles de sortie
  const effectiveChildCount = isQueue ? 0 : (isPrompt ? Math.max(1, childCount) : childCount);
  
  return (
    <>
      {/* Handle en haut pour recevoir des connexions */}
      <Handle
        type="target"
        position={Position.Top}
        id="target-top"
        style={{ 
          background: '#555',
          pointerEvents: 'all',
          cursor: 'crosshair'
        }}
      />
      
      {/* Handles en bas pour les connexions sortantes multiples */}
      {effectiveChildCount > 0 && Array.from({ length: effectiveChildCount }).map((_, index) => {
        // Distribution uniforme centrée, espacée uniformément
        const spacing = effectiveChildCount === 1 ? 0 : 60 / (effectiveChildCount + 1);
        const startOffset = effectiveChildCount === 1 ? 50 : 20;
        const position = effectiveChildCount === 1 ? 50 : startOffset + spacing * (index + 1);
        return (
          <Handle
            key={`source-${index}`}
            type="source"
            position={Position.Bottom}
            id={`source-${index}`}
            style={{
              left: `${position}%`,
              background: '#555',
              pointerEvents: 'all',
              cursor: 'crosshair'
            }}
          />
        );
      })}
      
      {children}
    </>
  );
};

/**
 * Générer les nœuds de routing de manière hiérarchique
 */
export const generateHierarchicalRoutingNodes = (
  callFlowData,
  sourceNodeId,
  startY,
  handlers,
  edgeIdStart
) => { 
  const allRoutings = callFlowData.routing || {};
  const generatedNodes = [];
  const generatedEdges = [];
  let edgeId = edgeIdStart;
  
  // 1. Construire le graphe de dépendances
  const graph = buildRoutingGraph(callFlowData);
  
  // 2. Calculer les niveaux
  calculateLevels(graph);
  
  // 3. Organiser par niveaux
  const leveledRoutings = organizeByLevels(graph, allRoutings);

  // 4. Calculer les positions
  const positions = calculateHierarchicalPositions(leveledRoutings, startY, graph);

  // 5. Créer les nœuds de routing ET les nœuds terminaux
  const processedRoutings = new Set();
  
  positions.forEach((position, routingKey) => {
    // Vérifier si c'est un nœud terminal
    const graphNode = graph.get(routingKey);
    
    if (graphNode && graphNode.isTerminal) {
      // C'est un nœud terminal (queue, transfer, disconnect)
      const terminalData = graphNode.terminalData;
      let NodeLabel, borderColor, nodeId;
      
      if (terminalData.type === 'queue') {
        NodeLabel = QueueNodeLabel;
        borderColor = '#10b981';
        nodeId = terminalData.queueId ? `queue-${terminalData.queueId}` : routingKey;
      } else if (terminalData.type === 'transfer' && terminalData.targetType === 'phone') {
        NodeLabel = TransferNodeLabel;
        borderColor = '#000000';
        nodeId = `transfer-pstn-${terminalData.targetId}`;
      } else if (terminalData.type === 'transfer' && terminalData.targetType === 'user') {
        NodeLabel = TransferNodeLabel;
        borderColor = '#92400e';
        nodeId = `transfer-user-${terminalData.targetId}`;
      } else if (terminalData.type === 'disconnect') {
        NodeLabel = DisconnectNodeLabel;
        borderColor = '#ef4444';
        nodeId = 'disconnect-node';
      }
      
      if (NodeLabel) {
        const childCount = graphNode.children ? graphNode.children.size : 0;
        const node = {
          id: nodeId,
          type: 'default',
          position: { x: position.x, y: position.y },
          deletable: true,
          data: {
            label: (
              <NodeWithHandles childCount={childCount} isQueue={terminalData.type === 'queue'}>
                <NodeLabel
                  optionData={terminalData}
                  handleEditNode={handlers.handleEditNode}
                  handleDeleteNode={handlers.handleDeleteNode}
                />
              </NodeWithHandles>
            ),
          },
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            border: `4px solid ${borderColor}`,
            borderRadius: '10px',
            padding: '15px',
            minWidth: '250px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        };
        generatedNodes.push(node);
      }
    } else {
      // C'est un routing normal
      const routing = allRoutings[routingKey];
      if (!routing || !routing.enabled) return;
      
      processedRoutings.add(routingKey);
      
      const routingId = `routing-${routingKey}`;
      const routingType = (routing.type || 'queue').toLowerCase();
      
      // Choisir le composant approprié selon le type
      let NodeLabel;
      let borderColor;
      
      if (routingType === 'ivr') {
        NodeLabel = IVRNodeLabel;
        borderColor = '#ec4899';
      } else if (routingType === 'queue') {
        NodeLabel = QueueNodeLabel;
        borderColor = '#10b981';
      } else if (routingType === 'prompt') {
        NodeLabel = PromptNodeLabel;
        borderColor = '#f97316';
      } else if (routingType === 'transfer') {
        NodeLabel = TransferNodeLabel;
        borderColor = '#92400e';
      } else if (routingType === 'disconnect') {
        NodeLabel = DisconnectNodeLabel;
        borderColor = '#ef4444';
      } else {
        NodeLabel = RoutingNodeLabel;
        borderColor = '#8b5cf6';
      }
      
      // Créer le nœud
      const childCount = graphNode.children ? graphNode.children.size : 0;
      const isPrompt = routingType === 'prompt';
      const isQueue = routingType === 'queue';
      const node = {
        id: routingId,
        type: 'default',
        position: { x: position.x, y: position.y },
        deletable: true,
        data: {
          label: (
            <NodeWithHandles childCount={childCount} isPrompt={isPrompt} isQueue={isQueue}>
              <NodeLabel
                routingKey={routingKey}
                routing={routing}
                handleEditNode={handlers.handleEditNode}
                handleDeleteNode={handlers.handleDeleteNode}
              />
            </NodeWithHandles>
          ),
        },
        style: {
          background: 'rgba(255, 255, 255, 0.95)',
          border: `4px solid ${borderColor}`,
          borderRadius: '10px',
          padding: '15px',
          minWidth: '250px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      };
      
      generatedNodes.push(node);
    }
  });
  
  // 6. Créer les edges entre parent et enfants
  graph.forEach((nodeInfo, routingKey) => {
    
    // Déterminer l'ID du parent
    const parentGraphNode = graph.get(routingKey);
    let parentId;
    
    if (parentGraphNode && parentGraphNode.isTerminal) {
      // Parent est un nœud terminal
      const terminalData = parentGraphNode.terminalData;
      if (terminalData.type === 'queue') {
        parentId = terminalData.queueId ? `queue-${terminalData.queueId}` : routingKey;
      } else if (terminalData.type === 'transfer' && terminalData.targetType === 'phone') {
        parentId = `transfer-pstn-${terminalData.targetId}`;
      } else if (terminalData.type === 'transfer' && terminalData.targetType === 'user') {
        parentId = `transfer-user-${terminalData.targetId}`;
      } else if (terminalData.type === 'disconnect') {
        parentId = 'disconnect-node';
      }
    } else {
      // Parent est un routing normal
      parentId = `routing-${routingKey}`;
    }
    
    const parentRouting = allRoutings[routingKey];
    if (!parentRouting || !parentRouting.enabled) {
      // Si pas de routing, c'est peut-être un terminal qui ne devrait pas avoir d'enfants
      if (!parentGraphNode || !parentGraphNode.isTerminal) return;
    }
    
    // Convertir les enfants en tableau et calculer les positions des handles
    const childrenArray = Array.from(nodeInfo.children);
    const childCount = childrenArray.length;
    
    childrenArray.forEach((childKey, childIndex) => {
      // Déterminer l'ID de l'enfant
      const childGraphNode = graph.get(childKey);
      let childId;
      
      if (childGraphNode && childGraphNode.isTerminal) {
        // Enfant est un nœud terminal
        const terminalData = childGraphNode.terminalData;
        if (terminalData.type === 'queue') {
          childId = terminalData.queueId ? `queue-${terminalData.queueId}` : childKey;
        } else if (terminalData.type === 'transfer' && terminalData.targetType === 'phone') {
          childId = `transfer-pstn-${terminalData.targetId}`;
        } else if (terminalData.type === 'transfer' && terminalData.targetType === 'user') {
          childId = `transfer-user-${terminalData.targetId}`;
        } else if (terminalData.type === 'disconnect') {
          childId = 'disconnect-node';
        }
      } else {
        // Enfant est un routing normal
        childId = `routing-${childKey}`;
      }
      
      const childRouting = allRoutings[childKey];
      if (!childRouting || !childRouting.enabled) {
        // Vérifier si c'est un terminal
        if (!childGraphNode || !childGraphNode.isTerminal) return;
      }
      
      // Déterminer le label de l'edge selon le type de parent
      let edgeLabel = '';
      let edgeColor = '#6366f1';
      
      if (parentRouting) {
        // Pour IVR, trouver le DTMF correspondant
        if (parentRouting.type === 'ivr' || parentRouting.type === 'IVR') {
          if (parentRouting.options) {
            Object.entries(parentRouting.options).forEach(([dtmf, option]) => {
              if (option.type === 'routing' && option.routingName === childKey) {
                edgeLabel = dtmf;
              } else if (childGraphNode && childGraphNode.isTerminal) {
                // Comparer les données terminales
                const terminalData = childGraphNode.terminalData;
                
                // Normaliser le type de l'option pour la comparaison
                const normalizedOptionType = option.type === 'transferUser' || option.type === 'transferPhone' ? 'transfer' : option.type;
                
                if (normalizedOptionType === terminalData.type) {
                  if (normalizedOptionType === 'queue' && option.queueId === terminalData.queueId) {
                    edgeLabel = dtmf;
                  } else if (normalizedOptionType === 'transfer' && option.targetId === terminalData.targetId) {
                    edgeLabel = dtmf;
                  } else if (normalizedOptionType === 'disconnect') {
                    edgeLabel = dtmf;
                  }
                }
              }
            });
          }
        }
        // Pour Prompt, c'est "Next"
        else if (parentRouting.type === 'prompt') {
          edgeLabel = 'Next';
          edgeColor = '#3b82f6';
        }
        // Pour Conditional, c'est "match" ou "unmatch"
        else if (parentRouting.type === 'check_variable' || parentRouting.type === 'time' || parentRouting.type === 'check_days' || parentRouting.type === 'regexp') {
          // Vérifier match
          if (parentRouting.onMatched?.type === 'routing' && parentRouting.onMatched?.routingName === childKey) {
            edgeLabel = 'match';
            edgeColor = '#10b981';
          } else if (childGraphNode && childGraphNode.isTerminal && parentRouting.onMatched) {
            const terminalData = childGraphNode.terminalData;
            const normalizedType = parentRouting.onMatched.type === 'transferUser' || parentRouting.onMatched.type === 'transferPhone' ? 'transfer' : parentRouting.onMatched.type;
            
            if (normalizedType === terminalData.type) {
              if ((normalizedType === 'queue' && parentRouting.onMatched.queueId === terminalData.queueId) ||
                  (normalizedType === 'transfer' && parentRouting.onMatched.targetId === terminalData.targetId) ||
                  (normalizedType === 'disconnect')) {
                edgeLabel = 'match';
                edgeColor = '#10b981';
              }
            }
          }
          
          // Vérifier unmatch
          if (parentRouting.onUnMatched?.type === 'routing' && parentRouting.onUnMatched?.routingName === childKey) {
            edgeLabel = 'unmatch';
            edgeColor = '#ef4444';
          } else if (childGraphNode && childGraphNode.isTerminal && parentRouting.onUnMatched) {
            const terminalData = childGraphNode.terminalData;
            const normalizedType = parentRouting.onUnMatched.type === 'transferUser' || parentRouting.onUnMatched.type === 'transferPhone' ? 'transfer' : parentRouting.onUnMatched.type;
            
            if (normalizedType === terminalData.type) {
              if ((normalizedType === 'queue' && parentRouting.onUnMatched.queueId === terminalData.queueId) ||
                  (normalizedType === 'transfer' && parentRouting.onUnMatched.targetId === terminalData.targetId) ||
                  (normalizedType === 'disconnect')) {
                edgeLabel = 'unmatch';
                edgeColor = '#ef4444';
              }
            }
          }
        }
      }
      
      // Calculer la position du handle source (distribué horizontalement)
      // Position relative: 0 = gauche, 0.5 = centre, 1 = droite
      let sourceHandlePosition;
      if (childCount === 1) {
        sourceHandlePosition = 0.5; // Centre
      } else {
        sourceHandlePosition = childIndex / (childCount - 1);
      }
      
      // Déterminer le type d'edge : éditable pour IVR, normal pour les autres
      const edgeType = parentRouting && (parentRouting.type === 'ivr' || parentRouting.type === 'IVR') 
        ? 'editableDTMF' 
        : 'custom';
      
      const edge = {
        id: `edge-${edgeId++}`,
        source: parentId,
        target: childId,
        sourceHandle: `source-${childIndex}`, // Handle spécifique pour chaque enfant
        targetHandle: 'target-top', // Handle en haut de l'enfant
        type: edgeType,
        animated: true,
        style: { stroke: edgeColor, strokeWidth: 2 },
        markerEnd: {
          type: 'arrowclosed',
          color: edgeColor,
        },
        label: edgeLabel,
        labelStyle: { fill: edgeColor, fontWeight: 600 },
        labelBgStyle: { fill: 'white' },
        data: {
          onDelete: handlers.onEdgeDelete,
          onLabelChange: handlers.onDTMFLabelChange,
          sourcePosition: sourceHandlePosition, // Position pour les handles dynamiques
          label: edgeLabel, // Garder le label dans data pour editableDTMF
        },
      };
      
      generatedEdges.push(edge);
    });
  });
  
  // 7. Créer l'edge depuis le nœud source vers routing "0"
  if (positions.has('0') && allRoutings['0']?.enabled) {
    const routing0Edge = {
      id: `edge-${edgeId++}`,
      source: sourceNodeId,
      target: 'routing-0',
      type: 'custom',
      animated: true,
      deletable: true,
      style: { stroke: 'rgb(98, 100, 167)', strokeWidth: 2 },
      markerEnd: {
        type: 'arrowclosed',
        color: 'rgb(98, 100, 167)',
      },
      data: {
        onDelete: handlers.onEdgeDelete,
      },
    };
    
    generatedEdges.push(routing0Edge);
  }
  
  return {
    nodes: generatedNodes,
    edges: generatedEdges,
    nextEdgeId: edgeId,
    processedRoutings,
    positions
  };
};
